import React from'react';
import clsx from'clsx';
import { formatLatency } from'../../utils/format';

/**
 * KPI metric card with optional comparison subtitle and click-to-drill-through.
 * @param {function} onClick - Click handler for drill-through navigation
 * @param {string} help - Plain-language explanation of the metric, shown as a hover tooltip.
 */
export default function KPICard({ title, value, subtitle, icon: Icon, color ='brand', format, onClick, help }) {
 const formattedValue = formatValue(value, format);

 const colorClasses = {
 brand:'bg-brand-50 text-brand-600',
 emerald:'bg-emerald-50 text-emerald-600',
 red:'bg-red-50 text-red-600',
 amber:'bg-amber-50 text-amber-600',
 violet:'bg-violet-50 text-violet-600',
 };

 return (
 <div
 className={clsx('kpi-card', onClick &&'cursor-pointer hover:ring-2 hover:ring-brand-300 transition-shadow')}
 onClick={onClick}
 role={onClick ?'button' : undefined}
 tabIndex={onClick ? 0 : undefined}
 onKeyDown={onClick ? (e) => { if (e.key ==='Enter') onClick(); } : undefined}
 >
 <div className="flex items-start justify-between mb-3">
 <span
 className="text-xs font-semibold text-gray-500 uppercase tracking-wide"
 title={help || undefined}
 >
 {title}
 {help && <span className="ml-1 text-gray-400 cursor-help" aria-label={help}>ⓘ</span>}
 </span>
 {Icon && (
 <div className={clsx('p-2 rounded-lg', colorClasses[color])}>
 <Icon className="w-4 h-4" />
 </div>
 )}
 </div>
 <div className="text-2xl font-bold tracking-tight">{formattedValue}</div>
 <div className="flex items-center justify-between mt-2">
 {subtitle && (
 <span className="text-xs text-gray-500">{subtitle}</span>
 )}
 </div>
 </div>
 );
}

function formatValue(value, format) {
 if (value == null) return'—';
 switch (format) {
 case'number':
 return Number(value).toLocaleString();
 case'currency': {
 const n = Number(value);
 if (!isFinite(n)) return'—';
 // For non-zero micro-costs (<$0.01), show enough decimals to be informative
 // instead of"$0.00". Cap at 4 fractional digits.
 if (n !== 0 && Math.abs(n) < 0.01) {
 return`$${n.toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`;
 }
 return`$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
 }
 case'percent':
 return`${Number(value).toFixed(1)}%`;
 case'ms':
 return formatLatency(value);
 case'tokens':
 if (value >= 1_000_000) return`${(value / 1_000_000).toFixed(1)}M`;
 if (value >= 1_000) return`${(value / 1_000).toFixed(1)}K`;
 return Number(value).toLocaleString();
 default:
 return String(value);
 }
}
